<template>
  <div class="Tenants">
    <b-row class="mb-4 align-items-end">
      <b-col class="text-left">
        <h1>Agences</h1>
      </b-col>

      <b-col cols="2" class="text-right"
        ><b-button
          class="Tenants-new-button"
          data-cy="Tenants-new-button"
          variant="outline-primary"
          :disabled="!canCreateTenants"
          @click="showNewTenantModal"
          ><i class="fas fa-plus"></i>&nbsp;Créer une nouvelle agence</b-button
        ></b-col
      >
    </b-row>
    <b-overlay :show="isLoading" spinner-variant="primary" opacity="1">
      <CustomTable
        class="Tenants-list p-0 text-left"
        v-if="canSearchTenants"
        filterable
        :items="tenants"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        @filtered="onFilterChanged"
        @sort-changed="onSortChanged"
        @page-changed="onPageChanged"
        @per-page-changed="onPerPageChanged"
      >
        <template v-slot:cell(actions)="data">
          <div class="text-center">
            <a
              href="#"
              v-if="canDeleteTenants"
              :data-cy="`delete-action-${data.item.name}`"
              @click="deleteTenant(data.item.name)"
            >
              <i class="fas fa-trash" />
            </a>
          </div>
        </template>
        <template #cell(name)="data">
          {{ data.item.name }}
        </template>
        <template #empty>
          <h4 class="text-secondary text-center mt-1">
            Il n'y a actuellement pas d'agence
          </h4>
        </template>
      </CustomTable>
      <ListNotAllowed v-if="rights.length && !canSearchTenants" />
    </b-overlay>
    <new-tenant-modal :groups="groups" @form-submitted="createTenant" />
  </div>
</template>

<script>
import NewTenantModal from '@/components/tenants/NewTenantModal';
import ListNotAllowed from '@/components/ListNotAllowed';
import CustomTable from '@/components/CustomTable';
import { mapGetters, mapMutations } from 'vuex';
import { isActionAllowed } from '@/helpers/rights.js';

export default {
  name: 'Agencies',
  components: {
    NewTenantModal,
    ListNotAllowed,
    CustomTable,
  },

  computed: {
    ...mapGetters('auth', ['currentUserRights']),
    ...mapGetters('tenants', ['tenants', 'softTenants']),
    canSearchTenants() {
      return isActionAllowed(
        this.currentUserRights,
        'document',
        'search',
        'multi-tenancy',
        'tenants'
      );
    },
    canCreateTenants() {
      return (
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/tenant',
          'create'
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/tenant',
          'listGroups'
        )
      );
    },
    canDeleteTenants() {
      return isActionAllowed(
        this.currentUserRights,
        'multi-tenancy/tenant',
        'delete'
      );
    },
  },

  data() {
    return {
      rights: [],
      isLoading: false,
      agencies: [],
      groups: [],
      filter: null,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      sort: null,
      fields: [
        {
          key: 'longName',
          label: 'Nom',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'actions',
          label: '',
          thClass: 'bg-success text-light',
        },
      ],
    };
  },

  async mounted() {
    const promises = [];

    if (this.canSearchTenants) {
      promises.push(this.fetchTenants());
    }

    if (this.canCreateTenants) {
      promises.push(this.fetchGroups());
    }

    await Promise.all(promises);
  },

  methods: {
    ...mapMutations('tenants', ['ADD_TENANT', 'REMOVE_TENANT']),
    async fetchTenants() {
      const body = {};

      body.sort = this.sort || { name: { order: 'asc' } };

      if (this.filter) {
        body.query = {
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: ['name.text', 'group.text'],
          },
        };
      }

      this.isLoading = false;
    },
    async fetchGroups() {
      const request = {
        controller: 'multi-tenancy/tenant',
        action: 'listGroups',
      };

      try {
        const response = await this.$kuzzle.query(request);
        this.groups = response.result.groups;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
    },
    handleDeleteConfirmation(name) {
      const customMessage = this.$createElement('p', [
        'Are you sure you want to delete the ',
        this.$createElement('code', name),
        ' tenant of group ',
        this.$createElement('code', 'pvstudio'),
      ]);
      return this.$bvModal.msgBoxConfirm(customMessage);
    },
    async deleteTenant(name) {
      try {
        const isDeleteConfirmed = await this.handleDeleteConfirmation(
          name,
          'pvstudio'
        );

        if (!isDeleteConfirmed) {
          return;
        }

        this.isLoading = true;

        const request = {
          controller: 'multi-tenancy/tenant',
          action: 'delete',
          name,
          group: 'pvstudio',
        };

        const response = await this.$kuzzle.query(request);

        this.REMOVE_TENANT(response.result.index);

        await this.fetchTenants();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async createTenant(formData) {
      try {
        this.isLoading = true;

        const name = this.sanitizeTenantName(formData.agencyName);
        const request = {
          controller: 'multi-tenancy/tenant',
          action: 'create',
          name,
          group: 'pvstudio',
          metadata: {
            longName: formData.agencyName,
          },
        };

        const response = await this.$kuzzle.query(request);
        const index = response.result.index;

        this.ADD_TENANT({
          _id: index,
          group: 'pvstudio',
          name,
          longName: formData.agencyName,
          index,
        });

        await this.fetchTenants();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async onFilterChanged(value) {
      this.filter = value;
      await this.fetchTenants();
    },
    async onSortChanged(value) {
      this.sort = {
        [value.sortBy]: { order: value.sortDesc ? 'desc' : 'asc' },
      };
      await this.fetchTenants();
    },
    async onPageChanged(value) {
      this.currentPage = value;
      await this.fetchTenants();
    },
    async onPerPageChanged(value) {
      this.perPage = value;
      await this.fetchTenants();
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Sorry, an error has occurred.',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true,
      });
    },
    showNewTenantModal() {
      this.$bvModal.show('new-tenant-modal');
    },
    sanitizeTenantName(name) {
      return name
        .toLowerCase()
        .split('')
        .filter((char) => /[a-z0-9]/.test(char))
        .join('');
    },
  },
};
</script>

<style scoped>
ul {
  list-style-type: none;
}
</style>
